import {
  faBoxesStacked,
  faClipboardList,
  faEnvelope,
  faPlugCircleBolt,
  faTruck,
} from '@fortawesome/pro-duotone-svg-icons'

import { MovePrepCategoryDescriptor, MovePrepTask } from '../_types/move-prep'

const MOVE_PREP_CATEGORIES: MovePrepCategoryDescriptor[] = [
  {
    category: 'paperwork',
    label: 'Paperwork',
    glyph: faClipboardList,
    blurb: 'Lease, references, insurance — the boring-but-required stuff.',
  },
  {
    category: 'utilities',
    label: 'Utilities',
    glyph: faPlugCircleBolt,
    blurb: 'Power, internet, water — get them transferred or set up.',
  },
  {
    category: 'address-change',
    label: 'Address change',
    glyph: faEnvelope,
    blurb: 'Banks, gov ID, subscriptions, employer — update your address.',
  },
  {
    category: 'packing',
    label: 'Packing',
    glyph: faBoxesStacked,
    blurb: 'Boxes, labels, declutter — start early to avoid the rush.',
  },
  {
    category: 'move-day',
    label: 'Move day',
    glyph: faTruck,
    blurb: 'The day-of essentials so nothing gets forgotten.',
  },
]

const MOVE_PREP_TASKS: MovePrepTask[] = [
  {
    id: 'sign-lease',
    category: 'paperwork',
    title: 'Sign the lease',
    hint: "Read every clause. Pet fees, parking, utilities — confirm what's included.",
  },
  {
    id: 'get-tenant-insurance',
    category: 'paperwork',
    title: 'Get tenant insurance',
    hint: 'Most landlords require it. $20–30/mo for typical coverage.',
  },
  {
    id: 'gather-references',
    category: 'paperwork',
    title: 'Line up references',
    hint: 'Previous landlord, employer, personal. Give them a heads up.',
  },
  {
    id: 'paystubs-ready',
    category: 'paperwork',
    title: 'Have last 3 paystubs ready',
    hint: "Or letter of employment if you're salaried.",
  },
  {
    id: 'first-and-last',
    category: 'paperwork',
    title: "Set aside first + last month's rent",
    hint: 'Plus any deposit. Have it as e-transfer or certified cheque on signing day.',
  },
  {
    id: 'utilities-power',
    category: 'utilities',
    title: 'Set up electricity',
    hint: 'Toronto Hydro, Hydro One, Alectra — depends on your address.',
  },
  {
    id: 'utilities-internet',
    category: 'utilities',
    title: 'Order internet',
    hint: 'Schedule install for move-in week. Some providers take 5–10 days.',
  },
  {
    id: 'utilities-water',
    category: 'utilities',
    title: 'Confirm water + heat',
    hint: 'Often included. If not, set them up with the city/utility.',
  },
  {
    id: 'utilities-gas',
    category: 'utilities',
    title: 'Transfer gas (if applicable)',
    hint: 'Enbridge for most of Ontario. Read the meter the day you take possession.',
  },
  {
    id: 'address-bank',
    category: 'address-change',
    title: 'Update your bank',
    hint: 'New address means new statements, new debit cards mailed if needed.',
  },
  {
    id: 'address-id',
    category: 'address-change',
    title: 'Update gov ID',
    hint: "Driver's licence, health card, voter registration.",
  },
  {
    id: 'address-cra',
    category: 'address-change',
    title: 'Update CRA',
    hint: 'Tax notices need to find you. Easy via My Account online.',
  },
  {
    id: 'address-subscriptions',
    category: 'address-change',
    title: 'Update recurring subscriptions',
    hint: 'Anything that ships physical mail. Magazines, gym, food box, etc.',
  },
  {
    id: 'address-employer',
    category: 'address-change',
    title: 'Tell your employer',
    hint: 'Payroll + HR for tax forms. Also union if applicable.',
  },
  {
    id: 'address-mail-redirect',
    category: 'address-change',
    title: 'Set up Canada Post mail redirect',
    hint: '$70 for a year. Catches anything you missed updating.',
  },
  {
    id: 'packing-declutter',
    category: 'packing',
    title: 'Declutter first',
    hint: "Don't pay to move things you don't want. Donate, sell, or trash early.",
  },
  {
    id: 'packing-supplies',
    category: 'packing',
    title: 'Get boxes + tape + labels',
    hint: 'LCBO + grocery stores have free boxes. Buy good tape — the cheap stuff fails.',
  },
  {
    id: 'packing-room-by-room',
    category: 'packing',
    title: 'Pack room-by-room with labels',
    hint: 'Mark "FRAGILE" + which room. Future-you will thank present-you.',
  },
  {
    id: 'packing-essentials-bag',
    category: 'packing',
    title: 'Pack a "first night" bag',
    hint: 'Toiletries, change of clothes, phone charger, toilet paper, snacks.',
  },
  {
    id: 'move-day-confirm-movers',
    category: 'move-day',
    title: 'Confirm movers / vehicle',
    hint: "Two days before. Check arrival window. Have backup if a friend's pickup truck.",
  },
  {
    id: 'move-day-parking',
    category: 'move-day',
    title: 'Arrange parking + elevator',
    hint: 'Many buildings need 24h notice for elevator booking and loading-zone permits.',
  },
  {
    id: 'move-day-meter',
    category: 'move-day',
    title: 'Photo the meter readings',
    hint: "Hydro + gas + water. Time-stamp them so utilities can't bill you for someone else.",
  },
  {
    id: 'move-day-walkthrough',
    category: 'move-day',
    title: 'Walk-through with landlord',
    hint: "Photo any pre-existing damage. Sign the inspection report only if it's honest.",
  },
  {
    id: 'move-day-first-clean',
    category: 'move-day',
    title: 'First-night clean',
    hint: 'Wipe down kitchen surfaces, bathroom, and fridge before you fully unpack.',
  },
]

export { MOVE_PREP_CATEGORIES, MOVE_PREP_TASKS }
